import React, { ReactElement } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { COLORS, FONT } from './tokens';

export interface ScopeOption<T extends string> {
  value: T;

  label: string;

  count?: number;
}

export interface ScopeToggleProps<T extends string> {
  value: T;
  options: [ScopeOption<T>, ScopeOption<T>];
  onChange: (value: T) => void;
  ariaLabel?: string;

  fullWidth?: boolean;
}

export const ScopeToggle = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  fullWidth = false,
}: ScopeToggleProps<T>): ReactElement => (
  <Box
    role="tablist"
    aria-label={ariaLabel}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      height: '48px',
      p: '3px',
      borderRadius: '8px',
      backgroundColor: COLORS.scopeTrackBg,
      border: `1px solid ${COLORS.filterBorder}`,
      ...(fullWidth
        ? { width: '100%' }
        : {
            flexShrink: 0,
            width: 'fit-content',
            maxWidth: '100%',
            alignSelf: 'flex-start',
          }),
    }}
  >
    {options.map(option => {
      const selected = option.value === value;
      const label =
        option.count === undefined
          ? option.label
          : `${option.label} · ${option.count}`;
      return (
        <ButtonBase
          key={option.value}
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(option.value)}
          disableRipple
          sx={{
            ...(fullWidth ? { flex: 1, minWidth: 0 } : {}),
            height: '100%',
            px: '14px',
            py: '6px',
            borderRadius: '6px',
            overflow: 'hidden',
            fontFamily: FONT,
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            transition: 'background-color 150ms ease, color 150ms ease',
            backgroundColor: selected ? COLORS.scopeActiveBg : 'transparent',
            color: selected ? COLORS.white : COLORS.navy,
            '&:hover': selected
              ? undefined
              : { backgroundColor: 'rgba(35,48,95,0.06)' },
            '&:focus-visible': {
              outline: `2px solid ${COLORS.navy}`,
              outlineOffset: '-2px',
            },
          }}
        >
          {label}
        </ButtonBase>
      );
    })}
  </Box>
);
